/**
 * Every permission a role can hold, grouped by area and described in plain
 * words. Must match the platform and vertical permission enums — a test checks the set,
 * because this list used to be missing landlord and payout permissions, so
 * no role could be given them from this screen.
 */
export const PERMISSION_GROUPS: { area: string; items: { code: string; label: string }[] }[] = [
  {
    area: '{Entities} and {payers}',
    items: [
      { code: 'beneficiaries:view', label: 'See {entities}, {payers} and balances' },
      { code: 'beneficiaries:manage', label: 'Add, edit and remove them' },
      { code: 'pay_links:manage', label: 'Create and revoke payment links for permitted accounts' },
    ],
  },
  {
    area: 'Payments',
    items: [
      { code: 'payments:view', label: 'See payments received' },
      { code: 'payments:prepare', label: 'Record cash and cheques, and prepare corrections for approval' },
      { code: 'payments:approve', label: "Give final approval to money decisions (never one's own)" },
      { code: 'fee_structures:view', label: 'See fee structures' },
      { code: 'fee_structures:manage', label: 'Change fee structures' },
    ],
  },
  {
    area: 'Waivers',
    items: [
      { code: 'waivers:request', label: 'Ask for a charge to be waived, with evidence' },
      { code: 'waivers:approve', label: "Approve, decline or undo waivers (never one's own request)" },
    ],
  },
  {
    area: 'Payment plans and promises',
    items: [
      { code: 'plans:view', label: 'See payment plans' },
      { code: 'plans:manage', label: 'Set up plans and record plan payments' },
      { code: 'plans:approve', label: 'Approve or decline plans and change requests' },
      { code: 'pledges:view', label: 'See promises to pay' },
      { code: 'pledges:manage', label: 'Manage promises to pay' },
    ],
  },
  {
    area: 'Landlords and payouts',
    items: [
      { code: 'owners:view', label: 'See landlords, expenses and payouts' },
      { code: 'owners:manage', label: 'Add landlords and buildings, record expenses, prepare payouts' },
    ],
  },
  {
    area: 'Messages',
    items: [
      { code: 'sessions:view', label: 'Read conversations' },
      { code: 'sessions:manage', label: 'Send messages in conversations' },
      { code: 'escalations:view', label: 'See what needs follow-up' },
      { code: 'escalations:manage', label: 'Assign and sort follow-ups' },
      { code: 'broadcasts:view', label: 'See bulk messages and templates' },
      { code: 'broadcasts:manage', label: 'Send bulk messages and edit templates' },
    ],
  },
  {
    area: 'Reports',
    items: [
      { code: 'reports:view', label: 'See reports, trends and collection totals' },
      { code: 'reports:export', label: 'Download reports and statements' },
      { code: 'segments:view', label: 'See payer groups' },
      { code: 'segments:manage', label: 'Change payer groups' },
    ],
  },
  {
    area: 'School day',
    items: [
      { code: 'classes:view', label: 'See classes' },
      { code: 'classes:manage', label: 'Manage classes' },
      { code: 'attendance:view', label: 'See attendance' },
      { code: 'attendance:manage', label: 'Take and correct attendance' },
      { code: 'timetable:view', label: 'See timetables, curriculum and lesson plans' },
      { code: 'timetable:manage', label: 'Edit timetables; use question banks and KNEC tools' },
      { code: 'assignments:view', label: 'See homework' },
      { code: 'assignments:manage', label: 'Set and mark homework' },
      { code: 'results:view', label: 'See exam results' },
      { code: 'results:manage', label: 'Enter and publish results' },
      { code: 'discipline:view', label: 'See behaviour records' },
      { code: 'discipline:manage', label: 'Record behaviour incidents' },
    ],
  },
  {
    area: 'Rental inventory and leases',
    items: [
      { code: 'properties:view', label: 'See assigned properties' },
      { code: 'properties:manage', label: 'Add or edit assigned properties' },
      { code: 'units:view', label: 'See units' },
      { code: 'units:finance_view', label: 'See unit financial details' },
      { code: 'units:manage', label: 'Add or edit units' },
      { code: 'leases:view', label: 'See leases' },
      { code: 'leases:manage', label: 'Create, change or end leases' },
      { code: 'maintenance:view', label: 'See maintenance requests' },
      { code: 'maintenance:create', label: 'Log maintenance requests' },
      { code: 'maintenance:manage', label: 'Update maintenance requests' },
      { code: 'messages:view', label: 'See messages sent to tenants and owners' },
      { code: 'messages:send', label: 'Send notices to tenants' },
    ],
  },
  {
    area: 'Rental finance',
    items: [
      { code: 'deposits:view', label: 'See tenant deposits' },
      { code: 'deposits:manage', label: 'Record deposit transactions' },
      { code: 'deposits:refund_request', label: 'Request deposit refunds' },
      { code: 'deposits:refund_approve', label: 'Approve deposit refunds' },
      { code: 'deposits:refund_disburse', label: 'Disburse approved refunds' },
      { code: 'penalties:view', label: 'See rental penalties' },
      { code: 'penalties:manage', label: 'Change rental penalties' },
      { code: 'rent_roll:generate', label: 'Generate a rent roll' },
      { code: 'charges:add', label: 'Add a one-off charge to a tenancy, with a reason' },
      { code: 'owner_expenses:prepare', label: 'Prepare owner expenses' },
      { code: 'owner_expenses:approve', label: 'Approve owner expenses' },
      { code: 'payouts:prepare', label: 'Prepare owner payouts' },
      { code: 'payouts:approve', label: 'Approve owner payouts' },
      { code: 'payouts:mark_paid', label: 'Mark owner payouts paid' },
    ],
  },
  {
    area: 'Organisation account',
    items: [
      { code: 'settings:view', label: 'See settings and plan & billing' },
      { code: 'settings:manage', label: 'Change settings' },
      { code: 'users:view', label: 'See the team' },
      { code: 'users:manage', label: 'Invite and manage the team' },
      { code: 'audit:view', label: 'See the activity log' },
    ],
  },
  {
    area: 'LipaFlow staff only',
    items: [
      { code: 'platform:admin', label: 'Use the admin console' },
      { code: 'platform:impersonate', label: "Sign in as a customer's user" },
      { code: 'tenant:view_as', label: 'View a customer as one of their team' },
    ],
  },
]

export const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.code))

const LABELS = new Map(PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => [i.code, { area: g.area, label: i.label }] as const)))

/** What a vertical calls the things it bills and the people who pay, in the plural. */
export interface PermissionWords { entities: string; payers: string }

const NEUTRAL: PermissionWords = { entities: 'accounts', payers: 'payers' }

function fill(text: string, words: PermissionWords) {
  return text
    .replace('{Entities}', words.entities.charAt(0).toUpperCase() + words.entities.slice(1))
    .replace('{entities}', words.entities)
    .replace('{payers}', words.payers)
}

/** A permission in plain words, and the area it belongs to, in the vertical's own words. */
export function describePermission(code: string, words: PermissionWords = NEUTRAL): { area: string; label: string } {
  const found = LABELS.get(code)
  return found ? { area: fill(found.area, words), label: fill(found.label, words) } : { area: 'Other', label: code.replace(/[:_]/g, ' ') }
}
